import type {
  PortalTherapistRole,
  ReadAccessTherapist,
  ReadAccessTherapistDTO,
  Therapist,
  TherapistDTO,
  HappinessRating,
  TermsAndConditions,
  TermsAndConditionsDTO,
  DTOEntity,
  DomainEntity,
  TherapistQuestionnaireTriggerCondition,
} from "@akina/domain";
import { toCamelCase } from "@akina/utils";
import type {
  TherapistFavouriteExercisesResponse,
  TherapistFavouriteExercisesResponseApi,
  TherapistFavouriteExercisesUpdateApi,
} from "types/schemas";
import { type AvailableLocales } from "../../locales";
import { readAccessTherapistMapper, termsAndConditionsMapper, therapistMapper } from "../../mappers";
import { ApiMethod, type ApiConfiguration } from "../shared";
import { uploadFileToS3 } from "../shared/uploadFileToS3";

export type InvitePatientDTO = {
  first_name: string;
  last_name: string;
  email: string;
  organization_id?: string;
  therapist_id?: string;
} & DTOEntity

export type InvitePatientFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  therapistId: string;
  clinicId: string;
  treatmentTemplateKey: string;
  language: (typeof AvailableLocales)[number];
  prescription?: File;
  dateOfBirth: Date;
} & DomainEntity

export const invitePatientMapper = {
  toDTO: (invitePatientDomain: InvitePatientFormValues) => ({
    first_name: invitePatientDomain.firstName,
    last_name: invitePatientDomain.lastName,
    email: invitePatientDomain.email,
    therapist_id: invitePatientDomain.therapistId,
    organization_id: invitePatientDomain.clinicId,
    treatment_template_key: invitePatientDomain.treatmentTemplateKey,
    language: invitePatientDomain.language,
    prescription_mime_type: invitePatientDomain?.prescription?.type,
    date_of_birth: invitePatientDomain.dateOfBirth,
  }),
};

export const therapistApi = (config: ApiConfiguration) => ({
  async getById(id: string): Promise<Therapist | null> {
    const result = await config.httpService.fetch<TherapistDTO | null>({
      method: ApiMethod.GET,
      url: `${config.endpoints.therapists.get}${id}/`,
    });

    if (!result) return null;

    return therapistMapper.toDomain(result);
  },
  async getMy(signal?: AbortSignal): Promise<ReadAccessTherapist> {
    const result = await config.httpService.fetch<ReadAccessTherapistDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.myTherapist,
      signal,
    });

    return readAccessTherapistMapper.toDomain(result);
  },

  async update(therapist: ReadAccessTherapist, signal?: AbortSignal) {
    await config.httpService.fetch<ReadAccessTherapistDTO>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.myTherapist,
      data: readAccessTherapistMapper.toDTO(therapist),
      signal,
    });
  },
  async updateProfilePicture(newProfilePicture: File): Promise<ReadAccessTherapist> {
    const result = await config.httpService.fetch<{ presigned_url: string }>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.profilePicture.uploadUrl,
      data: { file_type: newProfilePicture.type },
    });

    await uploadFileToS3(result.presigned_url, newProfilePicture);

    return await this.getMy();
  },
  async deleteProfilePicture(): Promise<ReadAccessTherapist> {
    await config.httpService.fetch<string>({
      method: ApiMethod.DELETE,
      url: config.endpoints.patients.profilePicture.delete,
    });

    return await this.getMy();
  },
  async getFavouriteExercises(signal?: AbortSignal): Promise<TherapistFavouriteExercisesResponse> {
    const response = await config.httpService.fetch<TherapistFavouriteExercisesResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.getFavouriteExercises,
      signal,
    });
    return toCamelCase(response);
  },

  async patchFavouriteExercises(exerciseKeys: string[], signal?: AbortSignal) {
    await config.httpService.fetch<TherapistFavouriteExercisesUpdateApi>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.getFavouriteExercises,
      data: { favourite_exercises: exerciseKeys },
      signal,
    });
  },

  async getTermsAcceptedStatus(id: string): Promise<TermsAndConditions> {
    const response = await config.httpService.fetch<TermsAndConditionsDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.getTermsAndConditions(id),
    });
    return termsAndConditionsMapper.toDomain(response);
  },

  async updateTermsAcceptedStatus(status: TermsAndConditions): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: `${config.endpoints.therapists.acceptTermsAndConditions}`,
      data: termsAndConditionsMapper.toDTO(status),
    });
  },
  async invitePatient(invitationDetails: InvitePatientFormValues): Promise<void> {
    const response = await config.httpService.fetch<{
      presigned_url: string;
    }>({
      method: ApiMethod.POST,
      url: config.endpoints.therapists.invitePatient,
      data: invitePatientMapper.toDTO(invitationDetails),
    });

    if (response && response.presigned_url && invitationDetails && invitationDetails.prescription) {
      await uploadFileToS3(response.presigned_url, invitationDetails.prescription);
    }
  },
  async delete(params: { therapistId: string }): Promise<string> {
    await config.httpService.fetch({
      method: ApiMethod.DELETE,
      url: config.endpoints.therapists.delete(params.therapistId),
    });
    return params.therapistId;
  },
  async editRole(params: {
    therapistId: string;
    role: PortalTherapistRole;
    onError?: (error: Error) => void;
  }): Promise<void> {
    try {
      await config.httpService.fetch({
        method: ApiMethod.PATCH,
        url: config.endpoints.therapists.editRole,
        data: {
          therapist_id: params.therapistId,
          role: params.role,
        },
      });
    } catch (error) {
      params.onError?.(error as Error);
    }
  },

  async addDevice(params: { token: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.devices.register,
      data: {
        token: params.token,
      },
    });
  },

  async removeDevice(params: { token: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.DELETE,
      url: config.endpoints.therapists.devices.unregister,
      data: {
        token: params.token,
      },
    });
  },
  async listDevices(): Promise<string[]> {
    return await config.httpService.fetch<string[]>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.devices.list,
    });
  },
  async sendReminder(params: { treatmentId: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: config.endpoints.therapists.sendReminder,
      data: {
        treatment_id: params.treatmentId,
      },
    });
  },
  async resendInvite(params: { treatmentId?: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: config.endpoints.therapists.resendInvite,
      data: {
        treatment_id: params.treatmentId,
      },
    });
  },
  async revokeInvite(params: { treatmentId?: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.revokeInvite,
      data: {
        treatment_id: params.treatmentId,
      },
    });
  },
  async submitHappinessFeedback({
    rating,
    comments,
    triggerCondition,
  }: {
    rating: HappinessRating;
    comments: string;
    triggerCondition: TherapistQuestionnaireTriggerCondition;
  }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: config.endpoints.therapists.happinessFeedback,
      data: {
        trigger_condition: triggerCondition,
        user_experience: rating,
        comments: comments,
      },
    });
  },
  async getDisplayHappinessQuestionnaire(params: {
    therapistId: string;
  }): Promise<{ triggerQuestionnaire: boolean; triggerCondition: TherapistQuestionnaireTriggerCondition }> {
    const result = await config.httpService.fetch<{
      trigger_questionnaire: boolean;
      trigger_condition: TherapistQuestionnaireTriggerCondition;
    }>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.displayHappinessQuestionnaire(params.therapistId),
    });

    return {
      triggerQuestionnaire: result.trigger_questionnaire,
      triggerCondition: result.trigger_condition,
    };
  },
});

export * from "./useDeleteProfilePicture";
export * from "./useDeleteTherapist";
export * from "./useEditTherapistRole";
export * from "./useFetchMyTherapist";
export * from "./useFetchTherapistsForClinic";
export * from "./useResendInvite";
export * from "./useRevokeInvite";
export * from "./useSendReminder";
export * from "./useUpdateMyTherapist";
export * from "./useUpdateProfilePicture";
export * from "./useFetchFavouriteExercises";
export * from "./usePatchFavouriteExercises";
